import { useEffect, useRef, useState } from 'react';

import { ICheckoutPageSettings } from '../../../../api/checkouts/types/checkout-page.type';
import { identifyBuyer, trackBehavior } from '../PageBlocks/behavior-tracker';
import { isPreviewMode } from '../PageBlocks/use-preview-bridge';
import { toast } from '../../../../utils/toast';
import './styles.scss';

/** Marcado pelo formulário enquanto o pagamento está sendo processado. */
export const SUBMITTING_FLAG = 'checkoutSubmitting';

/** Rolagem rápida para cima (rumo à barra de endereço) = intenção de sair. */
const SCROLL_UP_PX = 160;
const SCROLL_UP_WINDOW_MS = 450;
const MIN_SCROLLED_PX = 240;

/**
 * Exit intent: popup de retenção configurado no builder. Uma vez por sessão.
 * E-mail capturado identifica o comprador na inteligência de conversão — vira
 * alvo da recuperação de carrinho mesmo sem ter tocado no formulário.
 *
 * Sinais de saída:
 * - desktop: o mouse sai pelo topo da página (fechar aba / voltar);
 * - toque: não existe mouse, e o popup simplesmente nunca abria no celular —
 *   onde está a maior parte do tráfego. Lá valem dois sinais: voltar para a
 *   aba depois de tê-la deixado, e uma rolagem rápida para cima depois de já
 *   ter descido a página (o gesto de quem vai à barra de endereço).
 *
 * Nunca abre com o pagamento em processamento.
 */
export const ExitIntentPopup = ({
	settings
}: {
	settings?: ICheckoutPageSettings['exitIntent'];
}) => {
	const [open, setOpen] = useState(false);
	const [email, setEmail] = useState('');
	const [sent, setSent] = useState(false);
	const armedRef = useRef(false);

	useEffect(() => {
		if (!settings?.enabled || isPreviewMode()) return;
		const storageKey = `checkout-exit-intent:${window.location.pathname}`;
		try {
			if (sessionStorage.getItem(storageKey)) return;
		} catch {
			// sem storage, segue armado (no máximo mostra de novo)
		}

		// Arma só depois de uns segundos — abrir na entrada é anti-UX.
		const armTimer = window.setTimeout(() => {
			armedRef.current = true;
		}, 5000);

		const show = () => {
			if (!armedRef.current) return;
			// Pagamento em andamento: interromper agora só atrapalha.
			if (document.body.dataset[SUBMITTING_FLAG]) return;
			armedRef.current = false;
			try {
				sessionStorage.setItem(storageKey, '1');
			} catch {
				// segue sem persistir
			}
			setOpen(true);
			trackBehavior('exit_intent_shown', undefined, undefined, {
				once: true
			});
		};

		const onMouseOut = (event: MouseEvent) => {
			if (event.relatedTarget) return; // ainda dentro da página
			if (event.clientY > 8) return; // só saída pelo topo
			show();
		};

		// Toque: voltou para a aba depois de sair dela.
		let wasHidden = false;
		const onVisibility = () => {
			if (document.visibilityState === 'hidden') {
				wasHidden = armedRef.current;
				return;
			}
			if (wasHidden) show();
		};

		// Toque: rolagem rápida para cima depois de já ter descido.
		let maxScrolled = window.scrollY;
		let anchorY = window.scrollY;
		let anchorAt = Date.now();
		const onScroll = () => {
			const y = window.scrollY;
			const now = Date.now();
			maxScrolled = Math.max(maxScrolled, y);
			// Rolando para baixo, ou devagar demais: recomeça a janela.
			if (y >= anchorY || now - anchorAt > SCROLL_UP_WINDOW_MS) {
				anchorY = y;
				anchorAt = now;
				return;
			}
			if (maxScrolled >= MIN_SCROLLED_PX && anchorY - y >= SCROLL_UP_PX) {
				show();
			}
		};

		const isTouch =
			window.matchMedia?.('(hover: none) and (pointer: coarse)')
				.matches ?? false;

		document.addEventListener('mouseout', onMouseOut);
		if (isTouch) {
			document.addEventListener('visibilitychange', onVisibility);
			window.addEventListener('scroll', onScroll, { passive: true });
		}
		return () => {
			window.clearTimeout(armTimer);
			document.removeEventListener('mouseout', onMouseOut);
			document.removeEventListener('visibilitychange', onVisibility);
			window.removeEventListener('scroll', onScroll);
		};
	}, [settings?.enabled]);

	if (!settings?.enabled || !open) return null;

	const applyCoupon = () => {
		// Sem cupom o botão é só "continuar minha compra": fecha o popup.
		// Antes ele retornava aqui sem fechar e o botão ficava morto quando o
		// popup estava sem cupom e sem captura de e-mail.
		if (settings.couponCode) {
			// O campo de cupom escuta este evento e aplica como se digitado.
			window.dispatchEvent(
				new CustomEvent('waid:apply-coupon', {
					detail: settings.couponCode
				})
			);
		}
		setOpen(false);
	};

	const submitEmail = () => {
		const value = email.trim().toLowerCase();
		if (!value.includes('@')) {
			toast.error('Informe um e-mail válido.');
			return;
		}
		identifyBuyer(value);
		trackBehavior('exit_intent_email');
		setSent(true);
		if (settings.couponCode) applyCoupon();
	};

	return (
		<div
			className="exit-intent-backdrop"
			role="dialog"
			aria-modal="true"
			onClick={() => setOpen(false)}
		>
			<div
				className="exit-intent-card"
				onClick={(event) => event.stopPropagation()}
			>
				<button
					type="button"
					className="exit-intent-close"
					aria-label="Fechar"
					onClick={() => setOpen(false)}
				>
					×
				</button>
				<h3>{settings.title || 'Espere! Não vá embora ainda'}</h3>
				<p>
					{settings.message ||
						(settings.couponCode
							? 'Temos um desconto exclusivo para você concluir agora.'
							: 'Deixe seu e-mail e te ajudamos a concluir a compra.')}
				</p>

				{sent ? (
					<p className="exit-intent-success">
						✓ Combinado! Pode continuar sua compra.
					</p>
				) : (
					<>
						{settings.captureEmail !== false && (
							<input
								type="email"
								inputMode="email"
								placeholder="Seu melhor e-mail"
								value={email}
								onChange={(event) =>
									setEmail(event.target.value)
								}
							/>
						)}
						<button
							type="button"
							className="exit-intent-cta"
							onClick={
								settings.captureEmail !== false
									? submitEmail
									: applyCoupon
							}
						>
							{settings.buttonLabel ||
								(settings.couponCode
									? 'Quero meu desconto'
									: 'Continuar minha compra')}
						</button>
					</>
				)}
			</div>
		</div>
	);
};
